<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, useTemplateRef } from "vue";

import RichTextEditor from "@/components/ui/RichTextEditor.vue";
import SimpleButton from "@/components/ui/SimpleButton.vue";
import TextInput from "@/components/ui/TextInput.vue";
import { useModalsStore } from "@/stores/modals";

const modalsStore = useModalsStore();
const richTextEditor = useTemplateRef<InstanceType<typeof RichTextEditor>>("richTextEditor");
const modal = computed(() => modalsStore.getCurrentModal());
const promptResponse = ref("");

function onKeyDown(event: KeyboardEvent) {
  if (event.key === "Escape") {
    modal.value?.onCancel();
  } else if (event.key === "Enter") {
    if (modal.value?.type !== "prompt" && modal.value?.type !== "multiline-prompt") {
      modal.value?.onConfirm();
    }
  }
}

function cancel() {
  modal.value.onCancel();
  promptResponse.value = "";
}

function confirm() {
  modal.value.response = promptResponse.value;
  modal.value.onConfirm();
  promptResponse.value = "";
}

onMounted(() => {
  document.addEventListener("keydown", onKeyDown);
});

onUnmounted(() => {
  document.removeEventListener("keydown", onKeyDown);
});
</script>

<template>
  <Transition name="modal-appear">
    <div v-if="modal" class="container">
      <div class="dialog">
        <div class="header">
          <div class="label-m">
            <i v-if="(modal.titleIcon ?? '').length > 0" class="icon" :class="modal.titleIcon" />
            {{ modal.title }}
          </div>
          <SimpleButton icon-left="icon-close" @click="cancel" kind="tertiary" />
        </div>
        <div class="content">
          <div v-if="modal.message.length > 0" class="paragraph-s">{{ modal.message }}</div>
          <label class="prompt label-s" v-if="modal.type === 'prompt'">
            {{ modal.promptedValueName }}
            <TextInput v-model="promptResponse" :focus="true" @keydown.enter="confirm" />
          </label>
          <label class="prompt label-s" v-if="modal.type === 'multiline-prompt'">
            {{ modal.promptedValueName }}

            <div class="editor">
              <div class="format-actions">
                <div class="format" @click="richTextEditor?.markBold()">
                  <i class="icon icon-bold" />
                </div>
                <div class="format" @click="richTextEditor?.markItalic()">
                  <i class="icon icon-italic" />
                </div>
                <div class="format" @click="richTextEditor?.markList()">
                  <i class="icon icon-list" />
                </div>
              </div>
              <RichTextEditor ref="richTextEditor" v-model:value="promptResponse" :focus="true" />
            </div>
          </label>
        </div>
        <div class="actions alert" v-if="modal.type === 'alert'">
          <SimpleButton @click="confirm" :label="modal.confirmText" kind="primary" />
        </div>
        <div class="actions confirm" v-if="modal.type === 'confirm'">
          <SimpleButton @click="cancel" :label="modal.cancelText" kind="tertiary" />
          <SimpleButton @click="confirm" :label="modal.confirmText" kind="primary" />
        </div>
        <div
          class="actions prompt"
          v-if="modal.type === 'prompt' || modal.type === 'multiline-prompt'"
        >
          <SimpleButton @click="cancel" :label="modal.cancelText" kind="tertiary" />
          <SimpleButton
            @click="confirm"
            :label="modal.confirmText"
            kind="primary"
            :disabled="!promptResponse?.length"
          />
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.container {
  position: fixed;
  z-index: 9999;
  top: 0;
  left: 0;
  display: flex;
  width: 100dvw;
  height: 100dvh;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(4px);

  .dialog {
    position: relative;
    width: 32dvw;
    border: solid var(--stroke-width-m) var(--color-stroke-background-primary);
    border-radius: var(--radius-m);
    background-color: var(--color-background-primary);
    box-shadow: var(--box-shadow-l);

    .header {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      padding: var(--spacing-16);
      border-bottom: solid var(--stroke-width-m) var(--color-stroke-background-primary);

      .icon {
        padding: var(--spacing-4);
        border: solid var(--stroke-width-m) var(--color-stroke-background-primary);
        border-radius: var(--radius-s);
        margin-right: var(--spacing-4);
        color: var(--color-text-tertiary);
        vertical-align: middle;
      }
    }

    .content {
      display: flex;
      flex-direction: column;
      padding: var(--spacing-16);
      gap: var(--spacing-16);

      .prompt {
        display: flex;
        flex-direction: column;

        .text-input {
          background-color: var(--color-background-primary);
        }
      }

      .editor {
        height: 15dvh;
        padding: var(--spacing-4);
        border-radius: var(--radius-s);
        background-color: var(--color-background-secondary);

        .format-actions {
          display: flex;
          flex-direction: row;
          justify-content: flex-end;
        }
      }
    }

    .actions {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: flex-end;
      padding: 0 var(--spacing-16) var(--spacing-16) var(--spacing-16);
      gap: var(--spacing-16);

      .skore-button {
        flex-grow: 1;
      }
    }
  }
}

.modal-appear-enter-active,
.modal-appear-leave-active {
  transition: opacity var(--animation-duration) var(--animation-easing);

  & .dialog {
    transition: transform var(--animation-duration) var(--animation-easing);
  }
}

.modal-appear-enter-from,
.modal-appear-leave-to {
  opacity: 0;

  & .dialog {
    transform: translateY(33px);
  }
}
</style>
